import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle, ArrowRight, Check, CircleDashed, ExternalLink, RefreshCw, RotateCcw, ScanLine, X,
} from 'lucide-react';
import {
  deviceLane, activeSheetFor, resolvePaymentConfig, getPaymentConfig, paymentConfigOptionsFor,
} from '@platform/shared-types';
import {
  hardwareApi, type DeviceUnitRow, type DeviceOrderRow, type DeviceModelRow,
  type PendingReplacementRow, type ReplacementReason,
} from '../../services/hardwareApi';
import { inventoryApi } from '../../services/inventoryApi';
import { merchantApi } from '../../services/merchantApi';
import { api } from '../../services/apiService';
import { pushNotify } from '../../services/websocketService';
import { toastService } from '../../services/toastService';
import OverrideGate from '../pos/branding/OverrideGate';
import { registerForUnit, CLASS_FORM } from './DeviceSetupPanels';
import { unitLabel } from './stageShell';
import { readerBySerial, TENDER_FOR_CLASS } from './readerMatch';
import { RtpayModeCards, RtpayPairingCodePanel, isRtpayDevice, type RtpayRegisterMode } from '../../components/pos/RubyTillPayRegisterFields';
import {
  PRE_DISPATCH, canReplaceShipped, livePartnerProblem, OUTCOME_FOR_REASON, REASON_FOR_OUTCOME,
  cardTenderOf, withCardTender, resumeStepFor, osInstalledForReplacement, installUrlFor, replacementIdFor,
  type ReplaceStep, type ReplaceOutcome,
} from './replaceFlow';
import { RubyLoader } from '../../components/RubyLoader';
import { usableTerminalCode, formatExpiry } from '../../components/pos/TerminalNewCodeModal';

/**
 * REPLACE THIS DEVICE — one guided path, so nobody has to know the six screens
 * a swap used to take, or the order to visit them in.
 *
 * Every step is an EXISTING endpoint; this file only sequences them and says
 * what each one did. The sequence is the product: done out of order, a swap
 * leaves a till bound to a reader that left, a VAR sheet that cannot be bound
 * again ("one sheet, one reader"), or a device back on the shelf still paired
 * to a customer's till — all of which have happened.
 *
 *   1. What happened        good-but-swap / broken → Damage (RMA or destroy) / lost
 *   2. What it is tied to   discovered, shown, nothing changed yet
 *   3. Unlock               the emailed override code — release is gated on it
 *   4. Undo it all          FIRST recorded on the order as a pending replacement
 *                           (everything discovered in step 2), then release off
 *                           the order, till signed out, reader out of the pool,
 *                           VAR sheet freed, pairing undone
 *   5. Original condition   a good device is held OUT of stock until a reset
 *                           checklist is ticked; broken goes to Damage; lost is marked
 *   6. Pick the replacement same model, from stock, scanned or chosen
 *   7. Put it on the order  linked in both histories, then allocated (or, shipped
 *                           road, the field replacement is recorded)
 *   8. Install the OS       POS only: RubyTill is installed on the replacement from
 *                           Device management (another tab); the wizard waits for
 *                           the server's verified `osInstall` stamp
 *   9. Attach it            the register, the pairing and the VAR sheet move to it
 *  10. Card reader only:    register the new reader in the location's pool,
 *  11.                      then turn the till's card tender back on, bound to it
 *                           — and LAST, the pending replacement is completed
 *
 * TWO ROADS. Before dispatch this is a bench swap (release → reset → allocate).
 * A device that has SHIPPED cannot be released; it goes through the field
 * replacement endpoint instead (`replaceShippedUnit`), which moves it to
 * returned / lost / rma / retired and allocates the replacement with a
 * `replaces` link — but leaves the register, pool, sheet and pairing alone,
 * so steps 4 and 7–9 still hand those over. No unlock and no reset there.
 * Billing is never touched on either road.
 *
 * RESUMABLE. Because the pending replacement is on the order, a wizard closed
 * half-way is reopened from the order's banner in RESUME mode: it reads the
 * saved object (never re-discovers from a unit that is no longer attached)
 * and lands on the step `resumeStepFor` picks from live data.
 */

type Outcome = ReplaceOutcome;
type Step = ReplaceStep;
type TaskState = 'todo' | 'running' | 'done' | 'skipped' | 'failed';
interface Task { id: string; label: string; state: TaskState; detail?: string }
type TaskDef = { id: string; label: string; run: (() => Promise<string | void>) | null };

const OUTCOMES: { id: Outcome; title: string; desc: string }[] = [
  { id: 'good', title: 'It works — it just has to be swapped', desc: 'Wrong model, customer changed their mind, a test unit. It is reset to factory condition and goes back into stock.' },
  { id: 'rma', title: 'Broken — return to vendor', desc: 'Goes to Damage stock as an RMA, charged to the vendor.' },
  { id: 'destroy', title: 'Broken — destroy', desc: 'Beyond repair. Goes to Damage stock as a write-off.' },
  { id: 'lost', title: 'Lost', desc: 'We cannot find it. It is marked lost and leaves stock.' },
];

/** The shipped road's reasons — the server decides the state each one lands in. */
const SHIP_REASONS: { id: ReplacementReason; title: string; desc: string }[] = [
  { id: 'returned', title: 'The customer sent it back', desc: 'It is recorded as returned, for inspection when it arrives.' },
  { id: 'lost_in_transit', title: 'Lost in transit', desc: 'It never arrived. Recorded as lost — a carrier claim, not a return.' },
  { id: 'warranty', title: 'Faulty — warranty claim', desc: 'Recorded as an RMA back to the vendor. Whether it is in warranty is shown, not enforced.' },
  { id: 'damaged', title: 'Damaged', desc: 'Recorded as retired; the Damage flow decides who the cost falls on.' },
];

const STEP_LABEL: Record<Step, string> = {
  why: 'What happened',
  links: 'What it is tied to',
  unlock: 'Unlock',
  detach: 'Undo it all',
  restore: 'Original condition',
  pick: 'Pick the replacement',
  handover: 'Put it on the order',
  install: 'Install the OS',
  attach: 'Attach it',
  reader: 'Register the new reader',
  tender: 'Turn its card tender on',
  done: 'Done',
};

const TENDER_LABEL: Record<string, string> = {
  credit_dejavoo: 'Card — Dejavoo (SPIn USB)',
  credit_dejavoo_cloud: 'Card — Dejavoo (SPIn Cloud)',
  credit_dejavoo_rtpay: 'Card — RubyTill Pay',
  credit_nmi_cloud: 'Card — NMI Cloud',
  credit_nmi_direct: 'Card — NMI Direct',
};
const CLASS_FOR_TENDER: Record<string, string> = {
  credit_dejavoo: 'dejavoo_spin_usb',
  credit_dejavoo_cloud: 'dejavoo_spin_cloud',
  credit_dejavoo_rtpay: 'dejavoo_rubytill_pay',
};

/** What "original condition" means, per kind of device. Every line must be ticked. */
const RESET_CHECKLIST: Record<'pos' | 'payment' | 'other', string[]> = {
  pos: [
    'The till shows the RubyTill code-entry screen — it was signed out in the last step.',
    'Factory reset done: Settings → System → Reset options → Erase all data. It restarts to the Android welcome screen.',
    'Every customer label and sticker is removed.',
    'Checked: it powers on, both screens light, touch works, no cracks.',
    'Back in its box with the power supply and every cable it came with.',
  ],
  payment: [
    'Factory reset done from the reader’s admin menu — no merchant settings or TPN left on it.',
    'The customer’s TPN sticker and any labels are removed.',
    'Checked: it powers on and reads a test card, no damage.',
    'Back in its box with its cable.',
  ],
  other: [
    'Reset to factory settings.',
    'Every customer label and sticker is removed.',
    'Checked: it powers on and works, no damage.',
    'Back in its box with everything it came with.',
  ],
};

/** The two on-terminal steps RegisterEmvPanel gates pairing on, asked of THIS reader. */
const READER_READY = [
  'Setup configurations done — the new reader’s TPN has its profile in the processor’s portal.',
  'Parameters downloaded onto the new reader. One that has not pulled its profile pairs fine and fails at the first card.',
];

const orderName = (o: DeviceOrderRow | null) =>
  o?.orderNo || (o?.orderId ? `order ${o.orderId.replace(/^ord_/, '').slice(0, 8).toUpperCase()}` : 'its order');

const errText = (e: any, fallback: string) => e?.serverMessage || e?.message || fallback;

/**
 * NO TASK MAY SPIN FOREVER. A request that never answers (a dropped
 * connection, a tab put to sleep, a promise nobody settles) fails its task
 * after this long, with Retry. Retry is safe because every task checks
 * "already done" against live data before it writes.
 */
export const TASK_TIMEOUT_MS = 45_000;
export const TASK_TIMEOUT_TEXT = 'No answer after 45 seconds. Retry — finished steps are skipped.';

/** How often the Install step re-reads the replacement while it waits. */
const INSTALL_POLL_MS = 10_000;

const poolOf = (res: any): any[] => res?.devices || res?.items || (Array.isArray(res) ? res : []);

/** Drop undefined keys so the stored pending replacement carries only what was found. */
const compact = <T extends Record<string, any>>(o: T): T =>
  Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== null && v !== '')) as T;

const ReplaceDeviceWizard: React.FC<{
  /** The OUTGOING device. On resume only `unitId` is needed — it is re-read. */
  unit: Pick<DeviceUnitRow, 'unitId'> & Partial<DeviceUnitRow>;
  onClose: () => void;
  /** Called after anything changed, so the page can re-read the order. */
  onChanged?: () => void;
  /** RESUME MODE: the order's saved pending replacement for this unit. */
  resume?: PendingReplacementRow;
  /** The order the pending replacement lives on. Required with `resume`. */
  orderId?: string;
}> = ({ unit: initialUnit, onClose, onChanged, resume, orderId: resumeOrderId }) => {
  const [step, setStep] = useState<Step>('why');
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [reason, setReason] = useState('');
  /** Shipped road only: the closed reason the server takes. */
  const [shipReason, setShipReason] = useState<ReplacementReason | null>(null);
  const [mode, setMode] = useState<'pre' | 'post'>('pre');

  // ── What the device is tied to — read once, before anything changes ──────
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [unit, setUnit] = useState<DeviceUnitRow>(initialUnit as DeviceUnitRow);
  const [order, setOrder] = useState<DeviceOrderRow | null>(null);
  const [lane, setLane] = useState<'pos' | 'payment' | 'other'>('other');
  const [modelName, setModelName] = useState<string | null>(null);
  const [partner, setPartner] = useState<DeviceUnitRow | null>(null);
  /** pos lane: the device's own register. */
  const [register, setRegister] = useState<any | null>(null);
  /** payment lane: the paired till's register — a reader's register is its till's. */
  const [till, setTill] = useState<any | null>(null);
  const [cardTender, setCardTender] = useState<string | null>(null);
  const [poolDevice, setPoolDevice] = useState<any | null>(null);
  const [poolAmbiguous, setPoolAmbiguous] = useState(false);
  const [applicationId, setApplicationId] = useState<string | null>(null);
  const [sheet, setSheet] = useState<any | null>(null);
  /** Resume: a replacement already allocated for this swap, found from live data. */
  const [replacement, setReplacement] = useState<DeviceUnitRow | null>(null);
  const [restoreRecorded, setRestoreRecorded] = useState(false);
  /**
   * When this replacement started — the pending replacement's `startedAt` on
   * resume, or the moment this session recorded it. An OS install only counts
   * for the swap if it is at or after this (`osInstalledForReplacement`).
   */
  const [startedAt, setStartedAtState] = useState<number | null>(resume?.startedAt ?? null);
  /* Also in a ref: tasks are built when their button is pressed, so a task
     later in the same run reads the value an earlier task has just set. */
  const startedAtRef = useRef<number | null>(resume?.startedAt ?? null);
  const setStartedAt = (v: number) => { startedAtRef.current = v; setStartedAtState(v); };
  const [billingNote, setBillingNote] = useState<string | null>(null);
  /* The code to type on the replacement. The terminals list masks a claimed
     till's code (RT-****-AB12), so it is taken from the deregister response
     (re-armed, full) — or minted with "Generate new code" when none is known. */
  const [tillCode, setTillCode] = useState<{ code: string; expiresAt: string | null } | null>(null);
  const [tillCodeBusy, setTillCodeBusy] = useState(false);
  const keepCode = (resp: any, fallback?: any) => {
    const code = usableTerminalCode({ provisionCode: resp?.provisionCode }) || usableTerminalCode(fallback);
    if (code) setTillCode({ code, expiresAt: resp?.codeExpiresAt || fallback?.codeExpiresAt || null });
    return code;
  };
  const mintTillCode = async () => {
    if (!register?.terminalId || tillCodeBusy) return;
    setTillCodeBusy(true);
    try {
      const r = await api.posTerminals.newCode(register.terminalId, tenantId);
      setTillCode({ code: r.code, expiresAt: r.expiresAt || null });
    } catch (e: any) {
      toastService.error(e?.serverMessage || e?.message || 'Could not generate a new code.');
    } finally {
      setTillCodeBusy(false);
    }
  };

  const tenantId = order?.tenantId || unit.tenantId || '';
  const locationId = order?.locationId || unit.locationId || '';
  const resuming = !!resume;

  const readTerminals = async (tId: string): Promise<any[]> =>
    ((await api.posTerminals.list(tId).catch(() => null)) as any)?.terminals || [];

  /** The paired till's register and the device-backed tender it has on. */
  const readTill = async (tId: string, p: DeviceUnitRow | null, oId: string, terminals: any[]) => {
    if (!p || livePartnerProblem(p, oId)) return { till: null, tender: undefined as string | undefined };
    const t = registerForUnit(terminals, p) || null;
    if (!t) return { till: null, tender: undefined };
    const pt: any = await api.posNmi.getPaymentTypes(tId, t.terminalId).catch(() => null);
    return { till: t, tender: cardTenderOf(pt?.assigned || pt?.enabled) };
  };

  const discover = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const fresh = (await hardwareApi.getUnit(initialUnit.unitId)).unit;
      setUnit(fresh);
      if (!fresh.orderId) throw new Error('This device is not on an order — there is nothing to replace.');
      setMode(PRE_DISPATCH.has(fresh.state) ? 'pre' : 'post');
      const o = (await hardwareApi.getOrder(fresh.orderId)).order || null;
      setOrder(o);
      const model = fresh.modelId ? (await hardwareApi.getModel(fresh.modelId).catch(() => null))?.model : null;
      setModelName((model as any)?.modelName || (model as any)?.name || null);
      const l = deviceLane(model as any);
      const kind = l === 'pos' || l === 'payment' ? l : 'other';
      setLane(kind);
      const tId = o?.tenantId || fresh.tenantId || '';
      const lId = o?.locationId || fresh.locationId || '';

      const p = fresh.coupledUnitId ? (await hardwareApi.getUnit(fresh.coupledUnitId).catch(() => null))?.unit || null : null;
      setPartner(p);

      if (kind === 'pos' && tId) {
        setRegister(registerForUnit(await readTerminals(tId), fresh) || null);
      }
      if (kind === 'payment' && tId && lId) {
        /* By SERIAL only, and only when exactly one matches. Serials are not
           unique on this estate. */
        const list = poolOf(await api.posNmi.listEmvDevicesAtLocation(tId, lId).catch(() => null));
        const serial = String(fresh.serialNumber || '').trim();
        const matches = serial ? list.filter((d) => String(d.serialNumber || '').trim() === serial) : [];
        const dev = matches.length === 1 ? matches[0] : null;
        setPoolDevice(dev);
        setPoolAmbiguous(matches.length > 1);
        const app = (await merchantApi.ensureApplication(tId, lId).catch(() => null))?.application;
        setApplicationId(app?.applicationId || null);
        setSheet((app?.varSheets || []).find((v: any) => v.unitId === fresh.unitId && v.supersededAt === undefined) || null);
        const t = await readTill(tId, p, fresh.orderId, await readTerminals(tId));
        setTill(t.till);
        setCardTender(t.tender || (dev ? TENDER_FOR_CLASS[String(dev.deviceClass || '')] : undefined) || null);
      }
    } catch (e: any) {
      setLoadError(errText(e, 'Could not read this device.'));
    } finally {
      setLoading(false);
    }
  }, [initialUnit.unitId]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * RESUME — everything from the saved pending replacement, and the step from
   * live data. Nothing is re-discovered from the outgoing unit's links: by now
   * they have been undone, which is the whole reason they were saved.
   */
  const discoverResume = useCallback(async () => {
    if (!resume) return;
    setLoading(true);
    setLoadError(null);
    try {
      const p = resume;
      const oId = resumeOrderId || '';
      if (!oId) throw new Error('No order was given for this replacement.');
      const old = (await hardwareApi.getUnit(p.unitId)).unit;
      setUnit(old);
      const o = (await hardwareApi.getOrder(oId)).order || null;
      if (!o) throw new Error('Could not read the order this replacement is on.');
      setOrder(o);
      setMode(p.postDispatch ? 'post' : 'pre');
      setOutcome(p.outcome);
      setReason(p.reason);
      if (p.postDispatch) setShipReason(REASON_FOR_OUTCOME[p.outcome]);
      setLane(p.lane);
      const modelId = p.modelId || old.modelId;
      const model = modelId ? (await hardwareApi.getModel(modelId).catch(() => null))?.model : null;
      setModelName((model as any)?.modelName || (model as any)?.name || null);
      const tId = o.tenantId;
      const lId = o.locationId;

      const pt = p.partnerUnitId ? (await hardwareApi.getUnit(p.partnerUnitId).catch(() => null))?.unit || null : null;
      setPartner(pt);
      const terminals = p.terminalId ? await readTerminals(tId) : [];
      const savedReg = p.terminalId
        ? terminals.find((t) => t.terminalId === p.terminalId) || { terminalId: p.terminalId, provisionCode: p.provisionCode }
        : null;
      if (p.lane === 'pos') setRegister(savedReg);
      if (p.lane === 'payment') setTill(savedReg);
      setCardTender(p.cardTender || null);
      setPoolDevice(p.emvDeviceId ? { deviceId: p.emvDeviceId } : null);
      setSheet(p.varId ? { varId: p.varId } : null);
      setApplicationId(p.applicationId || null);

      // ── what live data says has already happened ──
      const timeline = (await hardwareApi.getUnitTimeline(p.unitId, { limit: 50 }).catch(() => null))?.items || [];
      const since = timeline.filter((e) => e.at >= (p.startedAt || 0));
      setRestoreRecorded(since.some((e) => e.kind === 'restored_to_stock'));
      const repId = replacementIdFor(p, old, timeline);
      const rep = repId ? (await hardwareApi.getUnit(repId).catch(() => null))?.unit || null : null;
      // Placed: on the order, or (shipped road) already named as the replacement.
      const repPlaced = !!rep && (rep.orderId === o.orderId || (!!p.postDispatch && old.replacedByUnitId === rep.unitId));
      setStartedAt(p.startedAt || 0);

      let attachComplete = false;
      let readerInPool = false;
      if (repPlaced && rep) {
        const regOk = p.lane !== 'pos' || !savedReg || savedReg.deviceUnitId === rep.unitId;
        const pairOk = !pt || !!livePartnerProblem(pt, o.orderId) || rep.coupledUnitId === pt.unitId;
        let sheetOk = true;
        if (p.varId && p.applicationId) {
          const app = (await merchantApi.ensureApplication(tId, lId).catch(() => null))?.application;
          sheetOk = !!activeSheetFor((app?.varSheets || []) as any, rep.unitId);
        }
        attachComplete = regOk && pairOk && sheetOk;
        if (p.lane === 'payment') {
          const pool = poolOf(await api.posNmi.listEmvDevicesAtLocation(tId, lId).catch(() => null));
          readerInPool = !!readerBySerial(pool, rep.serialNumber).device;
        }
      }
      // The shipped road names its replacement on the old unit itself.
      if (rep && (repPlaced || p.postDispatch)) {
        setReplacement(rep);
        setChosenId(rep.unitId);
      }
      setStep(resumeStepFor({
        pending: p,
        old: { state: old.state, orderId: old.orderId, replacedByUnitId: old.replacedByUnitId },
        orderId: o.orderId,
        replacementOnOrder: repPlaced,
        osInstalled: osInstalledForReplacement(rep, p.startedAt || 0),
        attachComplete,
        readerInPool,
        restoreRecorded: since.some((e) => e.kind === 'restored_to_stock'),
      }));
    } catch (e: any) {
      setLoadError(errText(e, 'Could not read this replacement.'));
    } finally {
      setLoading(false);
    }
  }, [resume, resumeOrderId]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = resuming ? discoverResume : discover;
  useEffect(() => { load(); }, [load]);

  // ── Task runner — each step's work, ticking, stop on the first failure ───
  const [tasks, setTasks] = useState<Task[]>([]);
  const [running, setRunning] = useState(false);
  const setTask = (id: string, patch: Partial<Task>) =>
    setTasks((ts) => ts.map((t) => (t.id === id ? { ...t, ...patch } : t)));

  const runTasks = async (list: TaskDef[]) => {
    // Keep what already finished: Retry resumes at the failed task rather than
    // re-running a release that already spent its override code.
    const prior = new Map(tasks.map((t) => [t.id, t.state]));
    setTasks(list.map((t) => ({
      id: t.id, label: t.label,
      state: !t.run ? 'skipped' : prior.get(t.id) === 'done' ? 'done' : 'todo',
    })));
    setRunning(true);
    for (const t of list) {
      if (!t.run || prior.get(t.id) === 'done') continue;
      setTask(t.id, { state: 'running', detail: undefined });
      let timer: ReturnType<typeof setTimeout> | undefined;
      try {
        const detail = await Promise.race([
          t.run(),
          new Promise<never>((_, reject) => {
            timer = setTimeout(() => reject(new Error(TASK_TIMEOUT_TEXT)), TASK_TIMEOUT_MS);
          }),
        ]);
        clearTimeout(timer);
        setTask(t.id, { state: 'done', detail: detail || undefined });
      } catch (e: any) {
        clearTimeout(timer);
        setTask(t.id, { state: 'failed', detail: errText(e, 'This step failed.') });
        setRunning(false);
        onChanged?.();
        return false;
      }
    }
    setRunning(false);
    onChanged?.();
    return true;
  };

  const allDone = tasks.length > 0 && tasks.every((t) => t.state === 'done' || t.state === 'skipped');

  /** The partner, only while it is on this order and alive — see livePartnerProblem. */
  const livePartner = partner && !livePartnerProblem(partner, order?.orderId) ? partner : null;
  const reasonText = () => (mode === 'post'
    ? [SHIP_REASONS.find((r) => r.id === shipReason)?.title, reason.trim()].filter(Boolean).join(' — ')
    : reason.trim());

  // ── Step 4: undo everything ──────────────────────────────────────────────
  const detachTasks = (): TaskDef[] => [
    {
      id: 'record',
      label: 'Record the replacement on the order, so it can be finished later',
      /* FIRST, before anything is undone: once the device is off the order
         its links are gone, and this is where a resume reads them back from. */
      run: resuming ? null : async () => {
        /* Already recorded (a Retry after a timed-out answer): sending it again
           would re-stamp startedAt, and with it the line an OS install is
           measured against. */
        const saved = (await hardwareApi.getOrder(order!.orderId)).order?.pendingReplacements?.[unit.unitId];
        if (saved) {
          setStartedAt(saved.startedAt || Date.now());
          return 'Already recorded on the order.';
        }
        const sentAt = Date.now();
        const res = await hardwareApi.recordPendingReplacement(order!.orderId, compact({
          unitId: unit.unitId,
          outcome: outcome!,
          reason: reasonText(),
          lane,
          modelId: unit.modelId,
          terminalId: lane === 'pos' ? register?.terminalId : lane === 'payment' ? till?.terminalId : undefined,
          provisionCode: lane === 'pos' ? usableTerminalCode(register) || undefined : undefined,
          emvDeviceId: poolDevice?.deviceId,
          cardTender: cardTender || undefined,
          varId: sheet?.varId,
          applicationId: sheet ? applicationId || undefined : undefined,
          partnerUnitId: livePartner?.unitId,
          postDispatch: mode === 'post' ? true : undefined,
        }) as any);
        setStartedAt((res as any)?.order?.pendingReplacements?.[unit.unitId]?.startedAt || sentAt);
        return 'If this is closed half-way, the order shows a banner to continue from where it stopped.';
      },
    },
    {
      id: 'history',
      label: 'Write it in the device’s history',
      run: resuming ? null : async () => {
        const tl = (await hardwareApi.getUnitTimeline(unit.unitId, { limit: 20 }).catch(() => null))?.items || [];
        if (tl.some((e) => e.kind === 'replace_started' && e.meta?.orderId === order?.orderId && e.at >= (startedAtRef.current || 0))) {
          return 'Already in its history.';
        }
        await hardwareApi.addUnitEvent(unit.unitId, {
          kind: 'replace_started',
          label: `Being replaced on ${orderName(order)} — ${reasonText()}`,
          source: 'admin',
          meta: {
            orderId: order?.orderId, outcome, reason: reasonText(), postDispatch: mode === 'post',
            terminalId: (register || till)?.terminalId, emvDeviceId: poolDevice?.deviceId, varId: sheet?.varId,
            partnerUnitId: partner?.unitId,
          },
        });
      },
    },
    {
      id: 'release',
      label: mode === 'pre' ? `Take ${unitLabel(unit)} off ${orderName(order)}` : 'It stays on the order, struck through — it has shipped',
      run: mode === 'pre' ? async () => {
        const now = (await hardwareApi.getUnit(unit.unitId)).unit;
        if (now.orderId !== order?.orderId) return 'Already off the order.';
        const res = await hardwareApi.releaseUnit(unit.unitId, `Replace: ${reasonText()}`);
        return res.tier === 'wipe' ? 'Its setup checklist is cleared — the replacement starts its own.' : undefined;
      } : null,
    },
    {
      id: 'register',
      label: register ? `Sign the till out of register ${register.registerName || register.terminalId}` : 'No register on this device',
      run: register ? async () => {
        const nowReg = (await readTerminals(tenantId)).find((t) => t.terminalId === register.terminalId);
        if (nowReg && nowReg.deviceUnitId && nowReg.deviceUnitId !== unit.unitId) return 'Already signed out — the register is on another device.';
        const [, resp] = await Promise.all([
          pushNotify('terminal:revoke', { tenantId, locationId: register.locationId || locationId, terminalId: register.terminalId }, { source: 'super_admin' }),
          api.posTerminals.deregister(register.terminalId, tenantId, { deviceUnitId: null }),
        ]);
        const code = keepCode(resp);
        return code
          ? `The register keeps its settings. Code ${code} will be entered on the replacement.`
          : 'The register keeps its settings. Its activation code is shown at the end.';
      } : null,
    },
    {
      id: 'reader',
      label: poolDevice ? 'Take the reader out of the location’s card pool' : 'No card reader in the pool for this device',
      run: poolDevice ? async () => {
        const pool = poolOf(await api.posNmi.listEmvDevicesAtLocation(tenantId, locationId));
        if (!pool.some((d) => d.deviceId === poolDevice.deviceId)) return 'Already out of the pool.';
        await api.posNmi.deregisterEmvDevice(tenantId, locationId, poolDevice.deviceId, true);
        return 'The till’s card tender is switched off until the new reader is set up.';
      } : null,
    },
    {
      id: 'sheet',
      label: sheet ? 'Free its VAR sheet for the replacement' : 'No VAR sheet bound to this device',
      run: sheet && applicationId ? async () => {
        const app = (await merchantApi.ensureApplication(tenantId, locationId)).application;
        const now = (app?.varSheets || []).find((v: any) => v.varId === sheet.varId);
        if (now && now.unitId !== unit.unitId) return 'Already free.';
        await merchantApi.unbindVarSheet(tenantId, applicationId, sheet.varId);
        return 'Any TID keyed off it is removed — the new reader gets its own.';
      } : null,
    },
    {
      id: 'pair',
      label: partner ? `Unpair it from ${unitLabel(partner)}` : 'Not paired with anything',
      run: partner ? async () => {
        const mine = (await hardwareApi.getUnit(unit.unitId)).unit;
        if (mine.coupledUnitId) await hardwareApi.upsertUnit({ ...mine, coupledUnitId: null } as any);
        const theirs = (await hardwareApi.getUnit(partner.unitId)).unit;
        if (theirs.coupledUnitId === unit.unitId) await hardwareApi.upsertUnit({ ...theirs, coupledUnitId: null } as any);
      } : null,
    },
    {
      id: 'hold',
      label: mode === 'post' ? 'Where it goes is recorded at the hand-over'
        : outcome === 'good' ? 'Hold it out of stock until it is reset' : 'Record where it goes next (next step)',
      run: mode === 'pre' && outcome === 'good' ? async () => {
        /* Release put it back in stock, but it is not reset yet. On hold, the
           allocator cannot hand an un-wiped device to the next customer. */
        const now = (await hardwareApi.getUnit(unit.unitId)).unit;
        if (now.state === 'on_hold') return 'Already on hold.';
        await hardwareApi.setUnitStatus(unit.unitId, { state: 'on_hold', holdReason: 'Being reset after a replacement — not ready for stock' } as any);
      } : null,
    },
  ];

  // ── Step 5: original condition / damage / lost ───────────────────────────
  const [ticked, setTicked] = useState<boolean[]>([]);
  const checklist = RESET_CHECKLIST[lane];
  useEffect(() => { setTicked(checklist.map(() => false)); }, [lane]); // eslint-disable-line react-hooks/exhaustive-deps
  const [restoreBusy, setRestoreBusy] = useState(false);
  const [restored, setRestored] = useState(false);

  const finishRestore = async () => {
    setRestoreBusy(true);
    try {
      const now = (await hardwareApi.getUnit(unit.unitId)).unit;
      if (outcome === 'good') {
        if (now.state !== 'in_stock') await hardwareApi.setUnitStatus(unit.unitId, { state: 'in_stock' } as any);
        await hardwareApi.addUnitEvent(unit.unitId, {
          kind: 'restored_to_stock', label: 'Reset to factory condition and back in stock', source: 'admin',
          meta: { checklist },
        });
      } else if (outcome === 'rma' || outcome === 'destroy') {
        await inventoryApi.logDamage({
          unitId: unit.unitId,
          stage: 'order setup',
          reason: reason.trim(),
          chargedTo: outcome === 'rma' ? 'vendor' : 'writeoff',
          state: outcome === 'rma' ? 'rma' : 'retired',
          notes: `Replaced on ${orderName(order)}`,
        });
      } else if (outcome === 'lost') {
        await hardwareApi.setUnitStatus(unit.unitId, { state: 'lost', holdReason: reason.trim() } as any);
        // The status endpoint does not keep a reason for `lost` — the history does.
        await hardwareApi.addUnitEvent(unit.unitId, { kind: 'marked_lost', label: `Marked lost — ${reason.trim()}`, source: 'admin' });
      }
      setRestored(true);
      onChanged?.();
    } catch (e: any) {
      toastService.error(errText(e, 'Could not record that.'));
    } finally {
      setRestoreBusy(false);
    }
  };

  // ── Step 6: pick the replacement ─────────────────────────────────────────
  const [stock, setStock] = useState<DeviceUnitRow[] | null>(null);
  const [scan, setScan] = useState('');
  const [chosenId, setChosenId] = useState<string | null>(null);
  useEffect(() => {
    if (step !== 'pick' || !unit.modelId) return;
    hardwareApi.listUnits({ state: 'in_stock', modelId: unit.modelId, limit: 50 })
      .then((r) => setStock((r.items || []).filter((u) => u.unitId !== unit.unitId)))
      .catch(() => setStock([]));
  }, [step, unit.modelId, unit.unitId]);
  const scanned = useMemo(() => {
    const q = scan.trim().toLowerCase();
    if (!q || !stock) return null;
    return stock.find((u) => [u.trackingId, u.serialNumber].some((v) => String(v || '').trim().toLowerCase() === q)) || null;
  }, [scan, stock]);
  useEffect(() => { if (scanned) setChosenId(scanned.unitId); }, [scanned]);
  const picked = stock?.find((u) => u.unitId === chosenId) || null;
  /** The device being handed everything — picked now, or found on resume. */
  const next = picked || (replacement && replacement.unitId === chosenId ? replacement : null);

  const completeTask = (): TaskDef => ({
    id: 'complete',
    label: 'Mark the replacement finished on the order',
    run: async () => {
      await hardwareApi.completePendingReplacement(order!.orderId, unit.unitId, next?.unitId);
    },
  });

  // ── Step 7: put it on the order ──────────────────────────────────────────
  const handoverTasks = (): TaskDef[] => {
    const n = next!;
    const first: TaskDef[] = mode === 'pre'
      ? [
        {
          id: 'link',
          /* BEFORE the allocation: a resume finds the replacement from this
             line, so an allocation can never happen without it. */
          label: 'Link the two devices in their history',
          run: async () => {
            const tl = (await hardwareApi.getUnitTimeline(unit.unitId, { limit: 20 }).catch(() => null))?.items || [];
            if (tl.find((e) => e.kind === 'replaced_by')?.meta?.replacedByUnitId === n.unitId) return 'Already linked.';
            await hardwareApi.addUnitEvent(unit.unitId, {
              kind: 'replaced_by', label: `Replaced by ${unitLabel(n)} on ${orderName(order)}`, source: 'admin',
              meta: { replacedByUnitId: n.unitId, orderId: order?.orderId },
            });
            await hardwareApi.addUnitEvent(n.unitId, {
              kind: 'replaces', label: `Replaces ${unitLabel(unit)} on ${orderName(order)} — ${reasonText()}`, source: 'admin',
              meta: { replacesUnitId: unit.unitId, orderId: order?.orderId },
            });
          },
        },
        {
          id: 'allocate',
          label: `Put ${unitLabel(n)} on ${orderName(order)}`,
          run: async () => {
            const now = (await hardwareApi.getUnit(n.unitId)).unit;
            if (now.orderId === order!.orderId) return 'Already on the order.';
            const res = await hardwareApi.allocateOrder(order!.orderId, [n.unitId], { onlyPreferred: true, onlyModelIds: [n.modelId!] });
            const took = res.allocations.some((a) => a.allocatedUnitIds.includes(n.unitId));
            if (!took) throw new Error(`The order did not take ${unitLabel(n)} — it may no longer be in stock. Go back and pick another.`);
          },
        },
      ]
      : [
        {
          id: 'replace',
          label: `Record the field replacement and send ${unitLabel(n)} on ${orderName(order)}`,
          run: async () => {
            const old = (await hardwareApi.getUnit(unit.unitId)).unit;
            if (old.replacedByUnitId === n.unitId) {
              setBillingNote('The agreement and subscription belong to the order, not the device, and were not changed.');
              return 'Already recorded.';
            }
            if (old.replacedByUnitId) throw new Error(`${unitLabel(old)} was already replaced by another device — reload the order.`);
            const res = await hardwareApi.replaceShippedUnit(unit.unitId, {
              reason: shipReason!, ...(reason.trim() ? { note: reason.trim() } : {}), replacementUnitId: n.unitId,
            });
            setBillingNote(res.billingNote || 'Billing was not changed.');
            const w = res.warrantyAtClaim;
            return `${unitLabel(unit)} is now ${res.unit?.state || 'recorded'}. Warranty at claim: ${
              w?.inWarranty ? `in warranty${w.daysLeft != null ? `, ${w.daysLeft} days left` : ''}` : w?.warrantyUntil ? 'expired' : 'unknown'}.`;
          },
        },
      ];
    return first;
  };

  // ── Step 9: attach it — register, pairing, sheet, and (not a reader) finish
  const attachTasks = (): TaskDef[] => {
    const n = next!;
    return [
      {
        id: 'register',
        label: register ? `Give it register ${register.registerName || register.terminalId}` : 'No register to hand over',
        run: register ? async () => {
          const nowReg = (await readTerminals(tenantId)).find((t) => t.terminalId === register.terminalId);
          if (nowReg?.deviceUnitId === n.unitId) { keepCode(null, nowReg); return 'Already on it.'; }
          const resp = await api.posTerminals.deregister(register.terminalId, tenantId, { deviceUnitId: n.unitId });
          keepCode(resp, nowReg);
        } : null,
      },
      {
        id: 'pair',
        label: partner ? `Pair it with ${unitLabel(partner)}` : 'Nothing to pair with',
        run: partner ? async () => {
          const theirs = (await hardwareApi.getUnit(partner.unitId)).unit;
          /* Only a partner on THIS order and ALIVE — a lost or returned
             reader keeps its orderId, and must never be paired again. */
          const problem = livePartnerProblem(theirs, order?.orderId);
          if (problem) return problem;
          const mine = (await hardwareApi.getUnit(n.unitId)).unit;
          if (mine.coupledUnitId === theirs.unitId && theirs.coupledUnitId === mine.unitId) return 'Already paired.';
          await hardwareApi.upsertUnit({ ...mine, coupledUnitId: partner.unitId } as any);
          await hardwareApi.upsertUnit({ ...theirs, coupledUnitId: n.unitId } as any);
        } : null,
      },
      {
        id: 'sheet',
        label: sheet ? 'Bind the VAR sheet to it' : 'No VAR sheet to move',
        run: sheet && applicationId ? async () => {
          const app = (await merchantApi.ensureApplication(tenantId, locationId)).application;
          if (activeSheetFor((app?.varSheets || []) as any, n.unitId)) return 'Already bound.';
          await merchantApi.bindVarSheet(tenantId, applicationId, sheet.varId, n.unitId);
        } : null,
      },
      // A reader finishes on its own two steps; everything else finishes here.
      ...(lane === 'payment' ? [] : [completeTask()]),
    ];
  };

  // ── Step 8: install the OS (POS lane only) ───────────────────────────────
  /* The install happens in Device management, in another tab. This step only
     WATCHES for the server's verified stamp: every 10s, whenever this tab gets
     focus back, and on "Check again". */
  const [repNow, setRepNow] = useState<DeviceUnitRow | null>(null);
  const [installChecking, setInstallChecking] = useState(false);
  const [installError, setInstallError] = useState<string | null>(null);
  const nextId = next?.unitId;
  const checkInstall = useCallback(async () => {
    if (!nextId) return;
    setInstallChecking(true);
    try {
      const u = (await hardwareApi.getUnit(nextId)).unit;
      setRepNow(u);
      setInstallError(null);
    } catch (e: any) {
      setInstallError(errText(e, 'Could not read the replacement.'));
    } finally {
      setInstallChecking(false);
    }
  }, [nextId]);
  useEffect(() => {
    if (step !== 'install' || !nextId) return undefined;
    checkInstall();
    const every = window.setInterval(checkInstall, INSTALL_POLL_MS);
    const onFocus = () => { checkInstall(); };
    const onVisible = () => { if (document.visibilityState === 'visible') checkInstall(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(every);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [step, nextId, checkInstall]);
  const installSubject = repNow && repNow.unitId === nextId ? repNow : next;
  const osReady = osInstalledForReplacement(installSubject, startedAt || 0);
  const openInstall = () => { if (nextId) window.open(installUrlFor(nextId), '_blank'); };

  // ── Step 10: register the new reader in the pool ─────────────────────────
  const [readerPool, setReaderPool] = useState<any[] | null>(null);
  const [readerSheets, setReaderSheets] = useState<any[] | null>(null);
  const [nextModel, setNextModel] = useState<DeviceModelRow | null>(null);
  const [readerForm, setReaderForm] = useState<Record<string, string>>({});
  const [readerReady, setReaderReady] = useState<boolean[]>(READER_READY.map(() => false));
  const [readerLoadError, setReaderLoadError] = useState<string | null>(null);
  const [readerLoaded, setReaderLoaded] = useState(false);

  const loadReaderStep = useCallback(async () => {
    if (!next || !tenantId || !locationId) return;
    setReaderLoadError(null);
    try {
      const [pool, app, model] = await Promise.all([
        api.posNmi.listEmvDevicesAtLocation(tenantId, locationId).then(poolOf),
        merchantApi.ensureApplication(tenantId, locationId).then((r) => r.application).catch(() => null),
        next.modelId ? hardwareApi.getModel(next.modelId).then((r) => r.model).catch(() => null) : Promise.resolve(null),
      ]);
      setReaderPool(pool);
      setReaderSheets(app?.varSheets || []);
      setNextModel(model as any);
      setReaderLoaded(true);
    } catch (e: any) {
      setReaderLoadError(errText(e, 'Could not read this location’s device pool.'));
    }
  }, [next?.unitId, tenantId, locationId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (step === 'reader' || step === 'tender') loadReaderStep(); }, [step, loadReaderStep]);

  const readerMatch = readerPool && next ? readerBySerial(readerPool, next.serialNumber) : null;
  const readerSheet = next && readerSheets ? activeSheetFor(readerSheets as any, next.unitId) : undefined;

  /* The lane the reader is registered on — RegisterEmvPanel's `laneFor`. The
     till's old tender says which transport it was on, so that is the default
     when the model offers both. */
  const readerLane = useMemo(() => {
    if (!nextModel) return { options: [] as any[], cfg: null as any, chosenId: '' };
    const fallback: any = resolvePaymentConfig(nextModel as any);
    const options: any[] = typeof paymentConfigOptionsFor === 'function'
      ? paymentConfigOptionsFor(nextModel as any)
      : (fallback ? [fallback] : []);
    const fromTender = cardTender ? CLASS_FOR_TENDER[cardTender] : undefined;
    const want = readerForm.paymentConfigId || fromTender || fallback?.id || '';
    const chosen: any = options.some((o) => o.id === want) ? getPaymentConfig(want) : fallback;
    return { options, cfg: chosen || fallback, chosenId: chosen?.id || fallback?.id || '' };
  }, [nextModel, cardTender, readerForm.paymentConfigId]);
  const readerShape = readerLane.chosenId ? CLASS_FORM[readerLane.chosenId] : undefined;
  const readerValue = (key: string) =>
    readerForm[key] ?? (key === 'serialNumber' ? next?.serialNumber || '' : key === 'deviceName' ? (poolDevice?.deviceName || `Reader ${next?.serialNumber || unitLabel(next || unit)}`) : '');

  const registerReaderTask = (): TaskDef => ({
    id: 'pool',
    label: `Register ${next ? unitLabel(next) : 'the new reader'} in the location’s card pool`,
    run: async () => {
      const n = next!;
      const shape = readerShape!;
      const cfg = readerLane.cfg;
      const name = readerValue('deviceName').trim();
      if (!name) throw new Error('Give the reader a name.');
      const values: Record<string, string> = {};
      for (const f of shape.fields) {
        const raw = readerValue(f.key).trim();
        if (f.required && !raw) throw new Error(`${f.label} is required.`);
        if (raw && f.valid && !f.valid(raw)) throw new Error(`${f.label} does not look right.`);
        if (raw) values[f.key] = raw;
      }
      if (!!values.iposApiKey !== !!values.iposSecretKey) {
        throw new Error('Enter both the iPOS API Key and Secret Key, or leave both blank to use the platform’s.');
      }
      if (values.spinRegisterId && values.spinRegisterId === values.spinAuthKey) {
        throw new Error('The SPIn Register Id and Auth Key are different values — check the portal.');
      }
      // Already there (a retry after a slow response) — never register twice.
      const pool = poolOf(await api.posNmi.listEmvDevicesAtLocation(tenantId, locationId));
      const existing = readerBySerial(pool, values.serialNumber || n.serialNumber);
      if (existing.device) { setReaderPool(pool); return `Already in the pool as ${existing.device.deviceName || existing.device.deviceId}.`; }
      try {
        if (shape.cloud) {
          await api.posNmi.registerCloudProcessor(tenantId, locationId, {
            deviceName: name, spinTpn: values.spinTpn, iposAuthToken: values.iposAuthToken,
            iposApiKey: values.iposApiKey, iposSecretKey: values.iposSecretKey,
          });
        } else {
          await api.posNmi.registerEmvDeviceToLocation(tenantId, locationId, {
            deviceName: name,
            model: nextModel?.modelName,
            paymentConfigId: readerLane.chosenId,
            connectionType: cfg?.profile?.connectionType,
            ...values,
            ...(shape.rtpay ? { rtpayMode: (readerForm.rtpayMode || 'charge_only') as RtpayRegisterMode } : {}),
          } as any);
        }
      } catch (e: any) {
        const code = e?.error || e?.code;
        throw new Error(
          code === 'GATEWAY_NOT_CONFIGURED' ? 'Save this location’s gateway credentials before registering a device.'
            : code === 'NMI_ID_IN_USE' ? 'That identifier is already registered to another device.'
              : e?.serverMessage || 'Could not register this reader.',
        );
      }
      setReaderPool(poolOf(await api.posNmi.listEmvDevicesAtLocation(tenantId, locationId).catch(() => null)));
      return `${name} is in the pool.`;
    },
  });

  // ── Step 11: turn the till's card tender back on, bound to the new reader ──
  const [tillNow, setTillNow] = useState<{ till: any | null; enabled: string[]; pt: any } | null>(null);
  useEffect(() => {
    if (step !== 'tender' || !tenantId) return;
    let live = true;
    (async () => {
      const terminals = await readTerminals(tenantId);
      let t = till?.terminalId ? terminals.find((x) => x.terminalId === till.terminalId) || till : null;
      if (!t && livePartner) t = registerForUnit(terminals, livePartner) || null;
      const pt: any = t ? await api.posNmi.getPaymentTypes(tenantId, t.terminalId).catch(() => null) : null;
      if (live) setTillNow({ till: t, enabled: pt?.assigned || pt?.enabled || [], pt });
    })();
    return () => { live = false; };
  }, [step, tenantId, till?.terminalId, livePartner?.unitId]); // eslint-disable-line react-hooks/exhaustive-deps

  const newReader = readerMatch?.device || null;
  /* The saved tender, unless the new reader was enrolled on the other SPIn
     transport — the service refuses a tender the reader is not enrolled for. */
  const tenderToEnable = (() => {
    const byClass = newReader ? TENDER_FOR_CLASS[String(newReader.deviceClass || '')] : undefined;
    if (byClass && (!cardTender || CLASS_FOR_TENDER[cardTender])) return byClass;
    return cardTender || byClass || null;
  })();
  const tenderAlreadyOn = !!tillNow?.till && !!tenderToEnable && tillNow.enabled.includes(tenderToEnable)
    && newReader?.registerId === tillNow.till.terminalId;

  const tenderTasks = (): TaskDef[] => [
    {
      id: 'tender',
      label: tillNow?.till && tenderToEnable
        ? `Turn ${TENDER_LABEL[tenderToEnable] || tenderToEnable} on for register ${tillNow.till.registerName || tillNow.till.terminalId}, bound to the new reader`
        : 'No till to turn a card tender on for',
      run: tillNow?.till && tenderToEnable ? async () => {
        const t = tillNow.till;
        const pt: any = await api.posNmi.getPaymentTypes(tenantId, t.terminalId).catch(() => null);
        /* Without the register's current lists there is nothing safe to write
           — sending `[tender]` would wipe every other tender. */
        if (!pt || !Array.isArray(pt.assigned)) throw new Error('Could not read the register\u2019s payment types, so nothing was changed.');
        const now: string[] = pt.assigned;
        const pool = poolOf(await api.posNmi.listEmvDevicesAtLocation(tenantId, locationId));
        const match = readerBySerial(pool, next?.serialNumber);
        if (!match.device?.deviceId) throw new Error(match.problem || 'The new reader has no pool id yet.');
        /* Assigned and enabled are each swapped on their OWN list — enabled is
           the merchant's, and is never reset to the assigned one. Override is
           not sent (absent = keep). The server pushes the till. */
        const enabled = withCardTender(now, tenderToEnable);
        const merchantEnabled = withCardTender(Array.isArray(pt.enabled) ? pt.enabled : [], tenderToEnable);
        await api.posNmi.putPaymentTypes(tenantId, t.terminalId, {
          assigned: enabled, enabled: merchantEnabled, emvDeviceId: match.device.deviceId,
        });
        const dropped = now.filter((x) => !enabled.includes(x));
        return `Sent to the till — one that is offline picks it up at the next cashier sign-in.${
          dropped.length ? ` ${dropped.join(', ')} was switched off: a register takes card one way.` : ''}`;
      } : null,
    },
    completeTask(),
  ];

  // ── Navigation ───────────────────────────────────────────────────────────
  const go = (s: Step) => { setTasks([]); setStep(s); };
  const visibleSteps: Step[] = [
    'why', 'links',
    ...(mode === 'pre' ? ['unlock' as Step] : []),
    'detach',
    ...(mode === 'pre' ? ['restore' as Step] : []),
    'pick', 'handover',
    ...(lane === 'pos' ? ['install' as Step] : []),
    'attach',
    ...(lane === 'payment' ? ['reader' as Step, 'tender' as Step] : []),
    'done',
  ];
  const stepIndex = Math.max(0, visibleSteps.indexOf(step));
  const eligible = resuming || PRE_DISPATCH.has(unit.state) || canReplaceShipped(unit);
  const canLeaveWhy = mode === 'post' ? !!shipReason : !!outcome && reason.trim().length > 2;
  const restoreReady = outcome !== 'good' || ticked.every(Boolean);
  const readerFormReady = !!readerShape && !!readerSheet && readerReady.every(Boolean);

  const nextLabel: Record<Outcome, string> = {
    good: 'Reset done — back in stock',
    rma: 'Send to Damage stock — return to vendor',
    destroy: 'Send to Damage stock — destroy',
    lost: 'Mark it lost',
  };

  // ── Render ───────────────────────────────────────────────────────────────
  const Body = () => {
    if (loading) return <RubyLoader variant="section" label={resuming ? 'Reading where this replacement stopped…' : 'Reading everything this device is tied to…'} />;
    if (loadError) return <Blocker text={loadError} onRetry={load} />;
    if (!eligible && step === 'why' && tasks.length === 0) {
      return <Blocker text={`${unitLabel(unit)} is "${unit.state}". It can be replaced before it ships, or once it has shipped — not from here.`} />;
    }

    switch (step) {
      case 'why':
        if (mode === 'post') {
          return (
            <div className="space-y-4">
              <Section title={`${unitLabel(unit)} has shipped — why is it being replaced?`}>
                <div className="space-y-1.5">
                  {SHIP_REASONS.map((r) => (
                    <Choice key={r.id} on={shipReason === r.id} title={r.title} desc={r.desc}
                      onClick={() => { setShipReason(r.id); setOutcome(OUTCOME_FOR_REASON[r.id]); }} />
                  ))}
                </div>
              </Section>
              <Section title="Note — optional, kept with the replacement">
                <input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. screen cracked in the box, customer photo on file"
                  className="min-h-[44px] w-full rounded-lg border border-stone-300 px-3 text-[13px] text-stone-800 placeholder-stone-400 focus:border-rose-500 focus:outline-none focus:ring-2 focus:ring-rose-500/10"
                />
              </Section>
            </div>
          );
        }
        return (
          <div className="space-y-4">
            <Section title={`What happened to ${unitLabel(unit)}?`}>
              <div className="space-y-1.5">
                {OUTCOMES.map((o) => (
                  <Choice key={o.id} on={outcome === o.id} title={o.title} desc={o.desc} onClick={() => setOutcome(o.id)} />
                ))}
              </div>
            </Section>
            <Section title="Why — this goes in the device's history">
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. touch panel dead on arrival"
                className="min-h-[44px] w-full rounded-lg border border-stone-300 px-3 text-[13px] text-stone-800 placeholder-stone-400 focus:border-rose-500 focus:outline-none focus:ring-2 focus:ring-rose-500/10"
              />
            </Section>
          </div>
        );

      case 'links':
        return (
          <Section title="These are undone in the next steps — nothing has changed yet">
            <ul className="divide-y divide-stone-100 rounded-xl border border-stone-200">
              <Link label="Order" value={orderName(order)} />
              <Link label="Paired with" value={partner ? `${unitLabel(partner)}${livePartner ? '' : ` (${partner.state} — the replacement will not be paired with it)`}` : null} />
              {lane === 'pos' && <Link label="Register" value={register ? `${register.registerName || register.terminalId} · code ${register.provisionCode || '—'}` : null} />}
              {lane === 'payment' && <Link label="Card pool" value={poolDevice ? `Reader ${poolDevice.deviceId}${poolDevice.spinTpn || poolDevice.tpn ? ` · TPN ${poolDevice.spinTpn || poolDevice.tpn}` : ''}` : null} />}
              {lane === 'payment' && <Link label="VAR sheet" value={sheet ? (sheet.mid ? `MID ${sheet.mid}` : sheet.varId) : null} />}
              {lane === 'payment' && <Link label="Till" value={till ? `${till.registerName || till.terminalId}${cardTender ? ` · ${TENDER_LABEL[cardTender] || cardTender}` : ' · no card tender on'}` : null} />}
            </ul>
            {poolAmbiguous && (
              <p className="mt-2 text-[12px] font-medium text-amber-700">
                More than one reader in the pool has serial {String(unit.serialNumber || '').trim()}, so the wizard will not guess which one to remove.
                Remove the right one on the Card processing lane after this.
              </p>
            )}
            <p className="mt-3 text-[12px] text-stone-500">
              {mode === 'post'
                ? 'It has shipped, so it stays on the order, struck through, and moves to the state its reason names when the replacement is sent.'
                : outcome === 'good'
                  ? 'Afterwards it is held out of stock until you have reset it.'
                  : outcome === 'lost' ? 'Afterwards it is marked lost.' : 'Afterwards it goes to Damage stock.'}
            </p>
          </Section>
        );

      case 'unlock':
        return (
          <OverrideGate
            unitId={unit.unitId}
            intent={`take ${unitLabel(unit)} off ${orderName(order)} and replace it`}
            onUnlocked={() => go('detach')}
          />
        );

      case 'detach':
      case 'handover':
      case 'attach':
        return (
          <div className="space-y-3">
            {resuming && tasks.length === 0 && (
              <p className="rounded-lg bg-[#FFF1F3] px-3 py-2 text-[12px] text-[#9F1239]">
                Continuing the replacement of {unitLabel(unit)} — “{reason}”. Steps already done tick straight through.
              </p>
            )}
            <TaskList tasks={tasks} />
          </div>
        );

      case 'install': {
        const name = next ? unitLabel(next) : 'the replacement';
        const inst = installSubject?.osInstall;
        const regName = register ? `register ${register.registerName || register.terminalId}` : 'the register';
        return (
          <div className="space-y-3">
            <Section title={`Install RubyTill on ${name}`}>
              <p className="text-[12.5px] leading-relaxed text-stone-600">
                {name} is on {orderName(order)}, but it cannot take over {regName} until RubyTill is installed on it
                from <b>Device management → Install</b>. That opens in a new tab, so this wizard and the order stay open.
                When the install finishes, this step notices by itself.
              </p>
            </Section>
            {osReady && inst
              ? <Done text={`RubyTill ${inst.appBuild} installed on ${name} · ${new Date(inst.installedAt).toLocaleString()}`} />
              : (
                <div role="status" className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2">
                  <CircleDashed size={15} className="shrink-0 text-amber-700" />
                  <p className="min-w-0 flex-1 text-[12.5px] font-semibold text-amber-900">Waiting for OS installation on {name}</p>
                  <button type="button" onClick={checkInstall} disabled={installChecking}
                    className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg px-3 text-[12.5px] font-semibold text-amber-900 hover:bg-amber-100 disabled:opacity-50">
                    {installChecking ? <RubyGemMark size={13} drawing color="currentColor" /> : <RefreshCw size={13} />} Check again
                  </button>
                </div>
              )}
            {installError && <p className="text-[12px] font-medium text-rose-700">{installError}</p>}
            {!osReady && (
              <p className="text-[11.5px] text-stone-500">
                Not installing it now? Close this — the order shows a banner for {name}, and it brings you back here.
              </p>
            )}
          </div>
        );
      }

      case 'restore':
        if (outcome === 'good') {
          return (
            <Section title="Put it back to original condition — tick each one as you do it">
              <div className="space-y-1.5">
                {checklist.map((line, i) => (
                  <Choice key={i} square on={!!ticked[i]} title={line} disabled={restored}
                    onClick={() => setTicked((t) => t.map((v, j) => (j === i ? !v : v)))} />
                ))}
              </div>
              {restored && <Done text="Back in stock, with the reset recorded in its history." />}
            </Section>
          );
        }
        return (
          <Section title={outcome === 'lost' ? 'Mark it lost' : 'Send it to Damage stock'}>
            <p className="text-[12.5px] text-stone-600">
              {outcome === 'rma' && <>It is recorded as damaged and <b>returned to the vendor (RMA)</b>, charged to the vendor. It shows in <b>Inventory → Damage</b>, where the return is closed out.</>}
              {outcome === 'destroy' && <>It is recorded as damaged and <b>written off</b>. It shows in <b>Inventory → Damage</b>, where it is closed out as destroyed.</>}
              {outcome === 'lost' && <>It leaves stock as <b>lost</b>. The reason you gave is kept in its history.</>}
            </p>
            <p className="mt-2 text-[12px] text-stone-500">Reason: “{reason.trim()}”</p>
            {restored && <Done text="Recorded." />}
          </Section>
        );

      case 'pick':
        return (
          <div className="space-y-4">
            <Section title={`Scan or pick a ${modelName || 'device of the same model'} from stock`}>
              <div className="relative">
                <ScanLine size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                <input
                  value={scan}
                  onChange={(e) => setScan(e.target.value)}
                  placeholder="Scan its barcode or type the tracking id / serial"
                  autoFocus
                  className="min-h-[44px] w-full rounded-lg border border-stone-300 pl-9 pr-3 text-[13px] text-stone-800 placeholder-stone-400 focus:border-rose-500 focus:outline-none focus:ring-2 focus:ring-rose-500/10"
                />
              </div>
              {scan.trim() && stock && !scanned && (
                <p className="mt-1.5 text-[12px] font-medium text-amber-700">No in-stock {modelName || 'device'} matches that — check the label.</p>
              )}
            </Section>
            {stock === null
              ? <RubyLoader variant="inline" label="Reading stock…" />
              : stock.length === 0
                ? <Blocker text={`No ${modelName || 'device of this model'} is in stock. Receive one first, then come back — the order's banner brings you back to this step.`} />
                : (
                  <div className="space-y-1.5">
                    {stock.map((u) => (
                      <Choice key={u.unitId} on={chosenId === u.unitId} title={unitLabel(u)}
                        desc={u.serialNumber && u.serialNumber !== unitLabel(u) ? `Serial ${u.serialNumber}` : undefined}
                        onClick={() => setChosenId(u.unitId)} />
                    ))}
                  </div>
                )}
          </div>
        );

      case 'reader': {
        if (readerLoadError) return <Blocker text={readerLoadError} onRetry={loadReaderStep} />;
        if (!readerLoaded || !readerPool) {
          return <RubyLoader variant="section" label="Reading the location’s card pool…" />;
        }
        if (readerMatch?.device) {
          return (
            <div className="space-y-3">
              {tasks.length > 0 && <TaskList tasks={tasks} />}
              <Done text={`${unitLabel(next!)} is in the pool as ${readerMatch.device.deviceName || readerMatch.device.deviceId}${readerMatch.device.tpn || readerMatch.device.tpnMasked ? ` · TPN ${readerMatch.device.tpn || readerMatch.device.tpnMasked}` : ''}.`} />
              {isRtpayDevice(readerMatch.device) && (
                <RtpayPairingCodePanel tenantId={tenantId} deviceId={readerMatch.device.deviceId} />
              )}
            </div>
          );
        }
        // Duplicate serials: never guess which one this is.
        if (readerMatch?.problem && /carry serial/.test(readerMatch.problem)) return <Blocker text={readerMatch.problem} onRetry={loadReaderStep} />;
        const cfg = readerLane.cfg;
        if (!cfg || cfg.id === 'none' || !cfg.orderUi || !readerShape) {
          return (
            <Blocker text={!cfg || cfg.id === 'none'
              ? `${modelName || 'This model'} has no payment config. Set one in Device Management, then come back — the order's banner brings you back here.`
              : `${cfg.label} readers are registered from the POS terminal screen, not from here. Register it there, then come back to turn its tender on.`} />
          );
        }
        return (
          <div className="space-y-4">
            {tasks.length > 0 && <TaskList tasks={tasks} />}
            <Section title={`Register ${unitLabel(next!)} in the location’s card pool`}>
              <p className="rounded-lg bg-stone-50 px-3 py-2 text-[11.5px] leading-relaxed text-stone-600">{readerShape.blurb}</p>
              {readerLane.options.length > 1 && (
                <div className="mt-2 space-y-1.5">
                  <p className="text-[11px] font-semibold text-stone-600">How it connects</p>
                  {readerLane.options.map((o: any) => (
                    <Choice key={o.id} on={readerLane.chosenId === o.id} title={o.label} desc={o.hint}
                      onClick={() => setReaderForm((f) => ({ ...f, paymentConfigId: o.id }))} />
                  ))}
                </div>
              )}
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                <Field label="Device name" value={readerValue('deviceName')}
                  onChange={(v) => setReaderForm((f) => ({ ...f, deviceName: v }))} />
                {readerShape.fields.map((f) => {
                  const v = readerValue(f.key);
                  return (
                    <Field key={f.key} label={`${f.label}${f.required ? '' : ' · optional'}`} value={v} mono
                      placeholder={f.placeholder} hint={f.hint} bad={!!v.trim() && !!f.valid && !f.valid(v)}
                      numeric={f.key === 'spinTpn' || f.key === 'rtpayPin'}
                      onChange={(nv) => setReaderForm((p) => ({ ...p, [f.key]: nv }))} />
                  );
                })}
              </div>
              {readerShape.rtpay && (
                <div className="mt-2">
                  <p className="mb-1 text-[11px] font-semibold text-stone-600">What it does at this register</p>
                  <RtpayModeCards
                    value={(readerForm.rtpayMode || 'charge_only') as RtpayRegisterMode}
                    onChange={(m) => setReaderForm((p) => ({ ...p, rtpayMode: m }))}
                  />
                </div>
              )}
            </Section>
            <Section title="On the new reader — tick both first">
              <div className="space-y-1.5">
                {READER_READY.map((line, i) => (
                  <Choice key={i} square on={!!readerReady[i]} title={line}
                    onClick={() => setReaderReady((t) => t.map((v, j) => (j === i ? !v : v)))} />
                ))}
              </div>
            </Section>
            {readerSheet
              ? <p className="text-[11.5px] text-stone-500">VAR sheet bound: <b className="text-stone-700">{(readerSheet as any).fileName || readerSheet.varId}</b></p>
              : (
                <p className="rounded-lg border-l-[3px] border-amber-400 bg-amber-50 px-3 py-2 text-[11.5px] text-amber-900">
                  No VAR sheet is bound to {unitLabel(next!)}. Registering against a TID nobody was issued is how a reader ends up keyed to the wrong merchant —
                  bind one on the order’s Card processing lane, then come back.
                </p>
              )}
            <p className="text-[11.5px] text-stone-500">
              No TPN yet? Stop here. The order shows a banner for this replacement, and Continue brings you back to exactly this step.
            </p>
          </div>
        );
      }

      case 'tender': {
        if (!tillNow || !readerPool) {
          return <RubyLoader variant="section" label="Reading the till’s payment types…" />;
        }
        if (!newReader) return <Blocker text={readerMatch?.problem || 'The new reader is not in the pool yet.'} onRetry={loadReaderStep} />;
        if (!tillNow.till) {
          return (
            <div className="space-y-3">
              {tasks.length > 0 && <TaskList tasks={tasks} />}
              <Blocker text={livePartner
                ? `${unitLabel(livePartner)} has no register, so there is no card tender to turn on. Finish without it, and set payment types once the till has a register.`
                : 'No live till is paired with this reader, so there is no card tender to turn on. Finish without it; pair a till on the order and set its payment types there.'} />
            </div>
          );
        }
        return (
          <div className="space-y-3">
            <Section title="Turn the till’s card tender back on, bound to the new reader">
              <ul className="divide-y divide-stone-100 rounded-xl border border-stone-200">
                <Link label="Till" value={`${tillNow.till.registerName || tillNow.till.terminalId}${livePartner ? ` · ${unitLabel(livePartner)}` : ''}`} />
                <Link label="Reader" value={`${newReader.deviceName || newReader.deviceId} · serial ${newReader.serialNumber}`} />
                <Link label="Takes now" value={tillNow.enabled.length ? tillNow.enabled.map((t) => TENDER_LABEL[t] || t).join(', ') : null} />
              </ul>
              {tenderToEnable
                ? <div className="mt-2"><Choice on title={`Turn on ${TENDER_LABEL[tenderToEnable] || tenderToEnable}`}
                    desc={cardTender && cardTender !== tenderToEnable
                      ? `The till had ${TENDER_LABEL[cardTender] || cardTender}; the new reader is enrolled for this one instead.`
                      : cardTender ? 'The card tender this till had before its reader was taken out.' : 'The tender this reader is enrolled for.'}
                    onClick={() => { /* the only choice — the reader decides it */ }} /></div>
                : <Blocker text={`The new reader is registered as "${newReader.deviceClass || 'unknown'}", which has no card tender this wizard can turn on. Set it on the order’s payment types.`} />}
            </Section>
            {tenderAlreadyOn && tasks.length === 0 && <Done text="Already on and bound to the new reader." />}
            <TaskList tasks={tasks} />
          </div>
        );
      }

      case 'done':
        return (
          <div className="space-y-3">
            <Done text={`${next ? unitLabel(next) : 'The replacement'} is on ${orderName(order)} in place of ${unitLabel(unit)}.`} />
            {mode === 'post' && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-[12.5px] text-amber-900">
                <b>Billing was not changed and nothing was charged.</b>{' '}
                {billingNote || 'The agreement and subscription belong to the order, not the device.'}{' '}
                A person needs to review billing for this swap.
              </div>
            )}
            <Section title="What is left — the order shows each of these as a step">
              <ul className="list-disc space-y-1 pl-5 text-[12.5px] text-stone-700">
                {lane === 'pos' && (() => {
                  const code = tillCode?.code || usableTerminalCode(register) || usableTerminalCode(resume);
                  if (code) {
                    return (
                      <li>
                        On the new till, enter code <b className="font-mono tracking-wider">{code}</b> when it asks. The register keeps all its settings.
                        {tillCode?.expiresAt && <span className="text-stone-500"> Works once · expires {formatExpiry(tillCode.expiresAt)}.</span>}
                      </li>
                    );
                  }
                  return (
                    <li>
                      The new till needs a fresh activation code — the old one was used. The register keeps all its settings.
                      <div className="mt-1.5">
                        <button type="button" onClick={mintTillCode} disabled={tillCodeBusy || !register?.terminalId}
                          className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-black px-4 py-2 text-sm font-semibold text-white hover:bg-stone-800 disabled:opacity-40">
                          {tillCodeBusy ? <RubyLoader variant="inline" /> : 'Generate new code'}
                        </button>
                      </div>
                    </li>
                  );
                })()}
                {lane === 'payment' && <li>Check the new reader takes a test card on the till.</li>}
                {mode === 'post' && <li>Ship {next ? unitLabel(next) : 'the replacement'} to the customer.</li>}
                <li>Its setup checklist, label photo and test receipt start fresh on the order.</li>
              </ul>
            </Section>
          </div>
        );
      default:
        return null;
    }
  };

  const Footer = () => {
    const primary = (label: string, onClick: () => void, disabled = false) => (
      <button onClick={onClick} disabled={disabled || running}
        className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-[#DC143C] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#9F1239] disabled:opacity-40">
        {running && <RubyGemMark size={13} drawing color="currentColor" />}{label}{!running && <ArrowRight size={14} />}
      </button>
    );
    const secondary = (label: string, onClick: () => void) => (
      <button onClick={onClick} disabled={running}
        className="min-h-[44px] rounded-xl border border-stone-200 px-4 py-2.5 text-sm font-semibold text-stone-700 hover:bg-stone-50 disabled:opacity-40">
        {label}
      </button>
    );
    if (loading || loadError || !eligible) return null;
    const failed = tasks.some((t) => t.state === 'failed');
    switch (step) {
      case 'why': return primary('Next', () => go('links'), !canLeaveWhy);
      case 'links': return mode === 'pre' ? primary('Next — unlock', () => go('unlock')) : primary('Next', () => go('detach'));
      case 'unlock': return null; // OverrideGate advances on success
      case 'detach':
        if (allDone) return primary('Next', () => go(mode === 'pre' ? 'restore' : 'pick'));
        return primary(failed ? 'Retry from the failed step' : resuming ? 'Continue undoing it' : 'Undo it all', () => { runTasks(detachTasks()); });
      case 'restore':
        if (restored) return primary('Next — pick the replacement', () => go('pick'));
        return primary(outcome ? nextLabel[outcome] : 'Next', finishRestore, !restoreReady || restoreBusy);
      case 'pick': return primary('Next — put it on the order', () => go('handover'), !picked);
      case 'handover':
        if (allDone) {
          return lane === 'pos'
            ? primary('Next — install the OS', () => go('install'))
            : primary('Next — attach it', () => go('attach'));
        }
        if (!next) return primary('Pick the replacement', () => go('pick'));
        return primary(failed ? 'Retry from the failed step' : 'Put it on the order', () => { runTasks(handoverTasks()); });
      case 'install':
        if (!next) return primary('Pick the replacement', () => go('pick'));
        if (osReady) return primary('Attach it', () => go('attach'));
        return (
          <>
            {secondary('Finish later — close', onClose)}
            <button type="button" onClick={openInstall}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-[#DC143C] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#9F1239]">
              Open Device management → Install <ExternalLink size={14} />
            </button>
          </>
        );
      case 'attach':
        if (allDone) return primary(lane === 'payment' ? 'Next — register the new reader' : 'Finish', () => (lane === 'payment' ? go('reader') : setStep('done')));
        if (!next) return primary('Pick the replacement', () => go('pick'));
        return primary(failed ? 'Retry from the failed step' : 'Hand everything over', () => { runTasks(attachTasks()); });
      case 'reader':
        if (readerMatch?.device) return primary('Next — turn its card tender on', () => go('tender'));
        return (
          <>
            {secondary('Stop here — finish later', onClose)}
            {primary(failed ? 'Retry' : 'Register the new reader', () => { runTasks([registerReaderTask()]); }, !readerFormReady)}
          </>
        );
      case 'tender':
        if (allDone) return primary('Finish', () => setStep('done'));
        if (!tillNow || !readerPool || !newReader) return secondary('Stop here — finish later', onClose);
        if (!tillNow.till || !tenderToEnable) {
          return (
            <>
              {secondary('Stop here — finish later', onClose)}
              {primary('Finish without the tender', () => { runTasks([{ ...tenderTasks()[0]!, run: null }, completeTask()]); })}
            </>
          );
        }
        return primary(failed ? 'Retry from the failed step' : tenderAlreadyOn ? 'Finish the replacement' : 'Turn the card tender back on',
          () => { runTasks(tenderAlreadyOn ? [{ ...tenderTasks()[0]!, run: null }, completeTask()] : tenderTasks()); });
      case 'done': return primary('Close', onClose);
      default: return null;
    }
  };

  // Once anything has been undone, closing half-way is allowed but said out
  // loud — by the banner below, never a native confirm (platform rule).
  const midway = (resuming || visibleSteps.indexOf(step) >= visibleSteps.indexOf('detach')) && step !== 'done';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" onClick={(e) => e.stopPropagation()}>
      <div className="flex max-h-[92vh] w-full max-w-[640px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start gap-3 border-b border-stone-200 px-5 py-4">
          <div className="min-w-0 flex-1">
            <h3 className="text-[15px] font-bold text-stone-900">{resuming ? 'Continue replacing' : 'Replace'} {unitLabel(unit)}</h3>
            <p className="mt-0.5 text-[12px] text-stone-500">
              Step {stepIndex + 1} of {visibleSteps.length} — {STEP_LABEL[step]}
            </p>
          </div>
          <button onClick={onClose} aria-label="Close" className="grid h-11 w-11 place-items-center rounded-lg text-stone-400 hover:bg-stone-100 hover:text-stone-700">
            <X size={16} />
          </button>
        </div>
        <ol className="flex gap-1 border-b border-stone-100 px-5 py-2">
          {visibleSteps.map((s, i) => (
            <li key={s} className={`h-1.5 flex-1 rounded-full ${i < stepIndex ? 'bg-[#DC143C]' : i === stepIndex ? 'bg-[#DC143C]/50' : 'bg-stone-200'}`} title={STEP_LABEL[s]} />
          ))}
        </ol>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{Body()}</div>
        {midway && (
          <p className="border-t border-amber-100 bg-amber-50 px-5 py-2 text-[11.5px] text-amber-800">
            You can close and come back — the order shows a banner with Continue — but finish every step: a half-done swap leaves the order short a device.
          </p>
        )}
        <div className="flex items-center justify-end gap-2 border-t border-stone-200 px-5 py-3.5">{Footer()}</div>
      </div>
    </div>
  );
};

// ── Small pieces ─────────────────────────────────────────────────────────────

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div>
    <p className="mb-1.5 text-[10.5px] font-bold uppercase tracking-wide text-stone-400">{title}</p>
    {children}
  </div>
);

const Choice: React.FC<{ on: boolean; title: string; desc?: string; square?: boolean; disabled?: boolean; onClick: () => void }> = ({ on, title, desc, square, disabled, onClick }) => (
  <button type="button" onClick={onClick} disabled={disabled}
    className={`flex w-full min-h-[44px] items-start gap-3 rounded-lg border px-3 py-2 text-left disabled:opacity-60 ${
      on ? 'border-[#DC143C] bg-[#FFF1F3]' : 'border-stone-200 bg-white hover:bg-stone-50'}`}>
    <span className={`mt-[3px] grid h-4 w-4 shrink-0 place-items-center border ${square ? 'rounded' : 'rounded-full'} ${
      on ? 'border-[#DC143C] bg-[#DC143C]' : 'border-stone-300 bg-white'}`}>
      {on && (square ? <Check size={11} className="text-white" /> : <span className="h-1.5 w-1.5 rounded-full bg-white" />)}
    </span>
    <span className="min-w-0">
      <span className="block text-[12.5px] font-semibold text-stone-800">{title}</span>
      {desc && <span className="block text-[11px] text-stone-500">{desc}</span>}
    </span>
  </button>
);

const Field: React.FC<{
  label: string; value: string; onChange: (v: string) => void;
  placeholder?: string; hint?: string; mono?: boolean; bad?: boolean; numeric?: boolean;
}> = ({ label, value, onChange, placeholder, hint, mono, bad, numeric }) => (
  <label className="block">
    <span className="block text-[11px] font-semibold text-stone-600">{label}</span>
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      inputMode={numeric ? 'numeric' : undefined}
      className={`mt-1 min-h-[44px] w-full rounded-lg border px-3 text-[13px] text-stone-800 focus:border-rose-500 focus:outline-none focus:ring-2 focus:ring-rose-500/10 ${
        mono ? 'font-mono' : ''} ${bad ? 'border-red-300 bg-red-50' : 'border-stone-300'}`}
    />
    {hint && <span className="mt-0.5 block text-[10.5px] leading-relaxed text-stone-400">{hint}</span>}
  </label>
);

const Link: React.FC<{ label: string; value: string | null }> = ({ label, value }) => (
  <li className="flex min-h-[40px] items-center gap-3 px-3 py-2 text-[12.5px]">
    <span className="w-24 shrink-0 text-stone-400">{label}</span>
    <span className={value ? 'font-medium text-stone-800' : 'text-stone-400'}>{value || 'none'}</span>
  </li>
);

const TaskList: React.FC<{ tasks: Task[] }> = ({ tasks }) => (
  tasks.length === 0
    ? <p className="text-[12.5px] text-stone-500">Press the button below. Each step ticks off as it finishes; if one fails, it stops there and says why.</p>
    : (
      <ul className="space-y-1.5">
        {tasks.map((t) => (
          <li key={t.id} className={`rounded-lg border px-3 py-2 ${t.state === 'failed' ? 'border-rose-300 bg-rose-50' : 'border-stone-200'}`}>
            <div className="flex items-center gap-2 text-[12.5px]">
              {t.state === 'running' && <RubyGemMark size={14} drawing />}
              {t.state === 'done' && <Check size={14} className="shrink-0 text-emerald-600" />}
              {t.state === 'failed' && <AlertTriangle size={14} className="shrink-0 text-rose-600" />}
              {t.state === 'todo' && <CircleDashed size={14} className="shrink-0 text-stone-300" />}
              {t.state === 'skipped' && <span className="w-[14px] shrink-0 text-center text-stone-300">–</span>}
              <span className={t.state === 'skipped' ? 'text-stone-400' : 'font-medium text-stone-800'}>{t.label}</span>
            </div>
            {t.detail && <p className={`ml-[22px] mt-0.5 text-[11.5px] ${t.state === 'failed' ? 'text-rose-700' : 'text-stone-500'}`}>{t.detail}</p>}
          </li>
        ))}
      </ul>
    )
);

const Blocker: React.FC<{ text: string; onRetry?: () => void }> = ({ text, onRetry }) => (
  <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 text-[12.5px] text-amber-900">
    <p>{text}</p>
    {onRetry && (
      <button onClick={onRetry} className="mt-2 inline-flex min-h-[44px] items-center gap-1.5 text-[12.5px] font-semibold text-amber-900">
        <RotateCcw size={13} /> Try again
      </button>
    )}
  </div>
);

const Done: React.FC<{ text: string }> = ({ text }) => (
  <p className="mt-3 flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-[12.5px] font-medium text-emerald-800">
    <Check size={14} /> {text}
  </p>
);

export default ReplaceDeviceWizard;
